import React, { useState, useRef } from 'react';
import './MainCenterBar.css';
import centerbarImg from './centerbar3Red.png';
import centerbarImg2 from './centerbar3Blue.png';
import ButtonOneImg from './button1.png';
import circleCutoutImg from './circlecutout.png';
import gearPixelImg from './Gear_Pixel.png';
import headLogoPixelImg from './Head_Logo_Pixel2.png';
import LoginStatus from './login_components/loginStatus';
import { useHandleLogin } from './login_components/HandleLogin';

const SHOW_LOGIN = true;
const SHOW_SUBSCRIBE = false;

const MainCenterBar = ({ slideCenter, setShowPopup, setPopupType, onLoginSuccess }) => {
    const [loginStatus, setLoginStatus] = useState('Awaiting Login...');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [loginReady, setLoginReady] = useState(false);    // eyes off + gear stopped
    const [comboPlaying, setComboPlaying] = useState(false); // scripted dial sequence

    // Gear controlled rotation during combo
    const [gearAngle, setGearAngle] = useState(0);
    const [gearTransition, setGearTransition] = useState(''); // e.g. 'transform 900ms cubic-bezier(...)'

    // Wrap success so we can run combo before opening doors
    const onReadyThenComboThenOpen = useRef(null);
    onReadyThenComboThenOpen.current = async () => {
        // 1) turn off "logging" visuals
        setIsLoggingIn(false);
        setLoginReady(true);     // hides eyes, stops CSS spin
        setComboPlaying(true);   // ensure CSS spin is disabled while we animate ourselves

        // 2) Run the combo sequence
        await runCombination(setGearAngle, setGearTransition);

        // 3) End combo, open doors
        setComboPlaying(false);
        onLoginSuccess();
    };

    const handleLogin = useHandleLogin(
        setLoginStatus,
        setIsLoggingIn,
        () => onReadyThenComboThenOpen.current && onReadyThenComboThenOpen.current()
    );

    const handleButtonClick = (label) => {
        if (label === 'Login') handleLogin();
        else if (label === 'About Proxima') setPopupType('about');
        else if (label === 'Subscribe') setPopupType('subscribe');
        else if (label === 'AstroMart') window.open('https://www.astromart.gallery/', '_blank');
        else if (label === 'AstroLink') setPopupType('linktree');
    };

    const buttonLabels = [
        ...(SHOW_LOGIN ? ['Login'] : []),
        ...(SHOW_SUBSCRIBE ? ['Subscribe'] : []),
        'About Proxima',
        'AstroMart',
        'AstroLink',
    ];

    return (
        <div
            className={[
                'p-App-maincenterbar-container',
                slideCenter ? 'slide-down' : '',
                isLoggingIn ? 'logging' : '',
                loginReady ? 'ready' : '',
                comboPlaying ? 'combo' : '',
            ].join(' ').trim()}
            style={{ zIndex: 200 }}
        >
            <div className="p-App-centerbar-wrapper">
                <div className="p-App-centerbar-scaler">
                    <img
                        src={slideCenter ? centerbarImg2 : centerbarImg}
                        alt="Center Bar"
                        className="p-App-maincenterbar"
                    />

                    <div className="p-App-maincontent">
                        <LoginStatus message={loginStatus} active={isLoggingIn} />

                        <div className="p-App-layered-images">
                            <img src={circleCutoutImg} alt="Circle" className="layer layer-bottom" />

                            {/* Gear � CSS spin when idle/logging, inline transform when comboPlaying */}
                            <img
                                src={gearPixelImg}
                                alt="Gear"
                                className="layer layer-middle"
                                style={comboPlaying || loginReady ? { transform: `rotate(${gearAngle}deg)`, transition: gearTransition } : undefined}
                            />

                            {/* Solid dark grey circle between gear and head */}
                            <div className="layer layer-grey"></div>

                            <img src={headLogoPixelImg} alt="Logo" className="layer layer-top" />

                            {/* Eyes overlay (only during logging) */}
                            <div className="eye-layer">
                                <span className="eye left" />
                                <span className="eye right" />
                            </div>
                        </div>

                        {buttonLabels.map((label) => (
                            <button
                                key={label}
                                className="p-App-image-button"
                                onClick={() => handleButtonClick(label)}
                                disabled={isLoggingIn && label === 'Login'}
                            >
                                <img src={ButtonOneImg} alt={label} />
                                <span className="p-App-image-button__label">{label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MainCenterBar;


async function runCombination(setAngle, setTransition) {
    // map 0..100 to 0..360deg
    const toDeg = n => (n % 100) * 3.6;

    // utility wait
    const wait = ms => new Promise(res => setTimeout(res, ms));

    // current angle tracker (deg)
    let curr = 0;

    // drive a step with CSS transition, then wait for it to finish
    const step = async ({ dir, toNum, turns, duration, ease = 'cubic-bezier(0.25, 0.8, 0.25, 1)' }) => {
        const targetDeg = toDeg(toNum);                   // destination on the dial (0..360)
        const currMod = ((curr % 360) + 360) % 360;       // normalize
        let delta;

        if (dir === 'right') {
            // clockwise to target + extra full turns
            const needed = ((targetDeg - currMod) + 360) % 360;
            delta = turns * 360 + needed;                   // positive delta
        } else {
            const needed = ((currMod - targetDeg) + 360) % 360;
            delta = -(turns * 360 + needed);                // negative delta
        }

        curr += delta;

        setTransition(`transform ${duration}ms ${ease}`);
        setAngle(curr);
        await wait(duration + 40); // small buffer
    };

    // sequence: tweak durations/turns to taste
    await step({ dir: 'left', toNum: 50, turns: 2, duration: 1100 }); // spin right past a couple turns, land 50
    await step({ dir: 'right', toNum: 22, turns: 1, duration: 900 }); // left one turn, land 22
    await step({ dir: 'left', toNum: 12, turns: 0.5, duration: 1300 }); // right half turn, land 12
    await step({ dir: 'right', toNum: 90, turns: 1, duration: 900 });
    setTransition(''); // freeze in place
}
